import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Sparkles, Palette, LayoutDashboard, Users, Globe, Zap, Check, Upload, Play, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  // Visualizer state - now with sub-steps for wrap selection
  const [visualizerStep, setVisualizerStep] = useState(1);
  const [wrapSelectionStep, setWrapSelectionStep] = useState(1); // 1: Brand, 2: Finish, 3: Color
  const [carImage, setCarImage] = useState(null);
  const [carImageBase64, setCarImageBase64] = useState('');
  const [selectedWrap, setSelectedWrap] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedFinish, setSelectedFinish] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [masterWraps, setMasterWraps] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [sampleCars, setSampleCars] = useState([]);
  const fileInputRef = useRef(null);

  // Premium sample cars - BMW, Porsche, Lamborghini
  const premiumSampleCars = [
    { name: 'BMW M Series', url: 'https://customer-assets.emergentagent.com/job_vehicle-wrap-1/artifacts/nqdocdhw_C5CA1264-462B-40C8-BC65-0CF63342087C%202.jpg' },
    { name: 'Porsche 911', url: 'https://images.unsplash.com/photo-1693164526787-4963200aba01?w=800&q=80' },
    { name: 'Lamborghini', url: 'https://images.unsplash.com/photo-1532581140115-3e355d1ed1de?w=800&q=80' },
  ];

  // Available finishes
  const finishTypes = ['Gloss', 'Matte', 'Satin', 'Metallic', 'Brushed', 'Chrome', 'Carbon'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed demo studio
        await axios.post(`${API}/seed-demo`);
        
        // Fetch master catalog wraps (from Folienpartner24)
        const masterRes = await axios.get(`${API}/wraps/master-catalog`);
        setMasterWraps(masterRes.data);
        
        // Fetch manufacturers with counts
        const mfrRes = await axios.get(`${API}/wraps/manufacturers`);
        // Filter out manufacturers with 0 wraps
        const activeMfrs = mfrRes.data.manufacturers.filter(m => m.wrap_count > 0);
        setManufacturers(activeMfrs);
        
        setSampleCars(premiumSampleCars);
      } catch (err) {
        console.error(err);
        setSampleCars(premiumSampleCars);
      }
    };
    fetchData();
  }, []);

  // Get available finishes for selected brand
  const getAvailableFinishes = () => {
    if (!selectedBrand) return [];
    const brandWraps = masterWraps.filter(w => w.manufacturer === selectedBrand);
    const finishes = [...new Set(brandWraps.map(w => w.finish))];
    return finishTypes.filter(f => finishes.includes(f));
  };

  // Get wraps filtered by brand and finish
  const getFilteredWraps = () => {
    if (!selectedBrand || !selectedFinish) return [];
    return masterWraps.filter(w => 
      w.manufacturer === selectedBrand && 
      w.finish === selectedFinish
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setCarImage(event.target.result);
      setCarImageBase64(event.target.result);
      setVisualizerStep(2);
    };
    reader.readAsDataURL(file);
  };

  const handleSampleCarSelect = async (car) => {
    try {
      const response = await fetch(car.url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = (event) => {
        setCarImage(event.target.result);
        setCarImageBase64(event.target.result);
        setVisualizerStep(2);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      setCarImage(car.url);
      setCarImageBase64(car.url);
      setVisualizerStep(2);
    }
  };

  const handleWrapSelect = async (wrap) => {
    setSelectedWrap(wrap);
    setGenerating(true);

    try {
      const studioRes = await axios.get(`${API}/studios/public/demo`);
      const studioId = studioRes.data.id;

      const renderRes = await axios.post(`${API}/renders`, {
        studio_id: studioId,
        wrap_id: wrap.id,
        car_image_base64: carImageBase64,
        finish: wrap.finish || 'gloss'
      });

      const renderId = renderRes.data.id;
      await axios.post(`${API}/renders/${renderId}/generate`);

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/renders/${renderId}`);
          if (statusRes.data.status === 'completed' && statusRes.data.result_image_url) {
            clearInterval(pollInterval);
            setResultImage(statusRes.data.result_image_url);
            setGenerating(false);
            setVisualizerStep(3);
          } else if (statusRes.data.status === 'fallback' || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setGenerating(false);
            toast.error('Generation failed. Please try again.');
          }
        } catch (err) {
          clearInterval(pollInterval);
          setGenerating(false);
          toast.error('Something went wrong');
        }
      }, 2000);
    } catch (err) {
      setGenerating(false);
      toast.error('Failed to start generation');
    }
  };

  const resetVisualizer = () => {
    setVisualizerStep(1);
    setWrapSelectionStep(1);
    setCarImage(null);
    setCarImageBase64('');
    setSelectedWrap(null);
    setSelectedBrand(null);
    setSelectedFinish(null);
    setResultImage(null);
  };

  // Handle brand selection
  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSelectedFinish(null);
    setWrapSelectionStep(2);
  };

  // Handle finish selection
  const handleFinishSelect = (finish) => {
    setSelectedFinish(finish);
    setWrapSelectionStep(3);
  };

  // Go back in wrap selection
  const handleWrapSelectionBack = () => {
    if (wrapSelectionStep === 3) {
      setSelectedFinish(null);
      setWrapSelectionStep(2);
    } else if (wrapSelectionStep === 2) {
      setSelectedBrand(null);
      setWrapSelectionStep(1);
    }
  };

  const features = [
    { icon: Sparkles, title: 'AI Visualization', desc: 'Photoreal wrap previews in seconds' },
    { icon: Globe, title: 'White-label Site', desc: 'Premium branded website included' },
    { icon: Palette, title: 'Wrap Catalog', desc: 'Pre-loaded manufacturer colors' },
    { icon: Users, title: 'Lead Capture', desc: 'Convert browsers to buyers' },
    { icon: LayoutDashboard, title: 'Admin Panel', desc: 'Full control dashboard' },
    { icon: Zap, title: 'Instant Setup', desc: 'Import your existing site' },
  ];

  const stats = [
    { value: '50K+', label: 'Visualizations' },
    { value: '50+', label: 'Studios' },
    { value: '35%', label: 'Higher Conversions' },
    { value: '460+', label: 'Wrap Colors' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-bold text-xl tracking-tight text-white flex items-center gap-2">
              WrapViz
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/pricing" className="text-sm text-zinc-400 hover:text-[#FF3399] transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm text-zinc-400 hover:text-[#FF3399] transition-colors">About</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <button className="text-zinc-400 hover:text-white text-sm font-medium transition-colors">Login</button>
              </Link>
              <Link to="/signup">
                <button className="btn-skew bg-[#FF3399] text-black font-bold text-sm px-6 py-2 hover:bg-white transition-colors">
                  <span>Get Started</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden" data-testid="hero-section">
        {/* Background Elements */}
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF3399]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#A855F7]/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="badge-cyan inline-block mb-6">
                For Premium Wrap Studios
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter mb-6 leading-[0.9]">
                <span className="text-white">See your</span>
                <br />
                <span className="text-[#FF3399]">wrap</span>
                <span className="text-white"> before</span>
                <br />
                <span className="text-zinc-600">you commit.</span>
              </h1>
              
              <p className="text-lg text-zinc-400 mb-8 max-w-lg leading-relaxed">
                Upload a photo of your car, pick a wrap color, and see a photoreal preview in seconds. Close more high-ticket wraps.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <button className="btn-skew bg-[#FF3399] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-all group">
                    <span className="flex items-center gap-2">
                      Start Free Trial
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
                <button className="btn-skew border border-zinc-700 text-white font-medium uppercase tracking-wider px-8 py-4 hover:border-[#FF3399] hover:text-[#FF3399] transition-all group">
                  <span className="flex items-center gap-2">
                    <Play className="w-4 h-4" fill="currentColor" />
                    Watch Demo
                  </span>
                </button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mt-12 pt-8 border-t border-zinc-800">
                {stats.map((stat, i) => (
                  <div key={i}>
                    <div className="text-2xl font-bold text-white font-mono">{stat.value}</div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* Right Column - Visualizer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              data-testid="hero-visualizer"
            >
              <div className="relative">
                {/* Glow effect behind */}
                <div className="absolute -inset-4 bg-gradient-to-r from-[#FF3399]/20 via-transparent to-[#A855F7]/20 blur-2xl opacity-50" />
                
                <div className="relative glass-light border border-zinc-800 p-6">
                  {/* Step Indicator */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      {[1, 2, 3].map((s) => (
                        <div key={s} className="flex items-center gap-2">
                          <div className={`step-indicator ${visualizerStep > s ? 'completed' : visualizerStep === s ? 'active' : ''}`}>
                            {visualizerStep > s ? <Check className="w-4 h-4" /> : s}
                          </div>
                          {s < 3 && <div className={`w-8 h-px ${visualizerStep > s ? 'bg-[#FF3399]' : 'bg-zinc-700'}`} />}
                        </div>
                      ))}
                    </div>
                    <span className="font-mono-specs text-zinc-500">
                      {visualizerStep === 1 ? 'UPLOAD' : visualizerStep === 2 ? 'SELECT' : 'RESULT'}
                    </span>
                  </div>

                  {/* Step 1: Upload */}
                  {visualizerStep === 1 && (
                    <div className="text-center">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="border border-dashed border-zinc-700 p-12 cursor-pointer hover:border-[#FF3399] transition-colors group"
                      >
                        <Upload className="w-10 h-10 text-zinc-600 mx-auto mb-4 group-hover:text-[#FF3399] transition-colors" />
                        <p className="text-zinc-400 font-medium mb-2">Drop your car photo here</p>
                        <p className="text-sm text-zinc-600">PNG, JPG up to 10MB</p>
                      </div>
                      
                      {sampleCars.length > 0 && (
                        <div className="mt-6">
                          <p className="font-mono-specs text-zinc-500 mb-4">Or try a sample</p>
                          <div className="flex justify-center gap-3">
                            {sampleCars.slice(0, 3).map((car, i) => (
                              <img
                                key={i}
                                src={car.url}
                                alt={car.name}
                                onClick={() => handleSampleCarSelect(car)}
                                className="w-20 h-14 object-cover cursor-pointer border border-zinc-800 hover:border-[#FF3399] transition-all"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Select Wrap - Multi-step: Brand → Finish → Color */}
                  {visualizerStep === 2 && !generating && (
                    <div className="flex gap-4">
                      {/* Left: Car preview - full visibility */}
                      <div className="w-1/2 flex-shrink-0">
                        <div className="relative h-full min-h-[280px]">
                          <img 
                            src={carImage} 
                            alt="Your car" 
                            className="w-full h-full object-cover border border-zinc-800"
                          />
                          {/* Selection breadcrumb */}
                          <div className="absolute bottom-2 left-2 flex items-center gap-1 text-xs">
                            {selectedBrand && (
                              <span className="bg-[#FF3399]/20 text-[#FF3399] px-2 py-0.5 border border-[#FF3399]/30">
                                {selectedBrand}
                              </span>
                            )}
                            {selectedFinish && (
                              <>
                                <ChevronRight className="w-3 h-3 text-zinc-600" />
                                <span className="bg-zinc-800 text-zinc-300 px-2 py-0.5 border border-zinc-700">
                                  {selectedFinish}
                                </span>
                              </>
                            )}
                          </div>
                          <button 
                            onClick={resetVisualizer}
                            className="absolute top-2 left-2 text-xs bg-black/60 text-zinc-400 hover:text-[#FF3399] px-2 py-1 flex items-center gap-1"
                          >
                            <ChevronLeft className="w-3 h-3" /> Change
                          </button>
                        </div>
                      </div>

                      {/* Right: Selection panel */}
                      <div className="w-1/2 flex flex-col">
                        {/* Quick Pick - Popular wraps */}
                        {wrapSelectionStep === 1 && (
                          <div className="mb-3">
                            <p className="font-mono-specs text-[#FF3399] text-xs mb-2">🔥 QUICK PICK</p>
                            <div className="flex gap-2 overflow-x-auto pb-4">
                              {masterWraps.slice(0, 6).map((wrap) => (
                                <button
                                  key={wrap.id}
                                  onClick={() => handleWrapSelect(wrap)}
                                  data-name={wrap.name}
                                  className="wrap-swatch flex-shrink-0 w-11 h-11 border-2 border-zinc-700 overflow-hidden"
                                >
                                  <img src={wrap.image_url} alt={wrap.name} className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Step 2.1: Select Brand */}
                        {wrapSelectionStep === 1 && (
                          <div className="flex-1">
                            <p className="font-mono-specs text-zinc-500 mb-2 text-xs">1. SELECT BRAND</p>
                            <div className="grid grid-cols-2 gap-1">
                              {manufacturers.map((mfr) => (
                                <button
                                  key={mfr.name}
                                  onClick={() => handleBrandSelect(mfr.name)}
                                  className="p-2 border border-zinc-800 hover:border-[#FF3399] transition-colors text-left group"
                                >
                                  <span className="text-xs font-bold text-white group-hover:text-[#FF3399] transition-colors">
                                    {mfr.name}
                                  </span>
                                  <span className="text-[10px] text-zinc-600 ml-1">
                                    {mfr.wrap_count}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Step 2.2: Select Finish */}
                        {wrapSelectionStep === 2 && (
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-mono-specs text-zinc-500 text-xs">2. SELECT FINISH</p>
                              <button 
                                onClick={handleWrapSelectionBack}
                                className="text-[10px] text-zinc-500 hover:text-[#FF3399] flex items-center gap-1"
                              >
                                <ChevronLeft className="w-3 h-3" /> Back
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-1">
                              {getAvailableFinishes().map((finish) => (
                                <button
                                  key={finish}
                                  onClick={() => handleFinishSelect(finish)}
                                  className="p-2 border border-zinc-800 hover:border-[#FF3399] transition-colors text-center group"
                                >
                                  <span className="text-xs font-medium text-white group-hover:text-[#FF3399] transition-colors">
                                    {finish}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Step 2.3: Select Color */}
                        {wrapSelectionStep === 3 && (
                          <div className="flex-1 flex flex-col">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-mono-specs text-zinc-500 text-xs">3. SELECT COLOR</p>
                              <button 
                                onClick={handleWrapSelectionBack}
                                className="text-[10px] text-zinc-500 hover:text-[#FF3399] flex items-center gap-1"
                              >
                                <ChevronLeft className="w-3 h-3" /> Back
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-2 overflow-y-auto flex-1 max-h-[180px] pr-1 pt-6">
                              {getFilteredWraps().map((wrap) => (
                                <button
                                  key={wrap.id}
                                  onClick={() => handleWrapSelect(wrap)}
                                  data-name={wrap.name.replace(selectedBrand || '', '').trim()}
                                  className="wrap-swatch relative aspect-square border-2 border-zinc-800 overflow-hidden"
                                >
                                  {wrap.image_url ? (
                                    <img 
                                      src={wrap.image_url}
                                      alt={wrap.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-zinc-800" />
                                  )}
                                </button>
                              ))}
                            </div>
                            {getFilteredWraps().length === 0 && (
                              <p className="text-xs text-zinc-600 text-center py-4">
                                No wraps found
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Generating State */}
                  {generating && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 spinner mx-auto mb-6" />
                      <p className="text-lg font-medium text-white mb-2">Generating preview...</p>
                      <p className="font-mono-specs text-zinc-500">30-60 seconds</p>
                    </div>
                  )}

                  {/* Step 3: Result */}
                  {visualizerStep === 3 && resultImage && (
                    <div className="text-center">
                      <div className="relative mb-4">
                        <img 
                          src={resultImage} 
                          alt="Wrapped car preview" 
                          className="w-full border border-zinc-800"
                        />
                        <div className="absolute top-2 right-2 badge-lime">
                          AI Generated
                        </div>
                      </div>
                      <p className="text-lg font-medium text-white mb-4">
                        Your car in <span className="text-[#FF3399]">{selectedWrap?.name}</span>
                      </p>
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={resetVisualizer}
                          className="btn-skew border border-zinc-700 text-white font-medium px-6 py-3 hover:border-[#FF3399] hover:text-[#FF3399] transition-all"
                        >
                          <span>Try Another</span>
                        </button>
                        <Link to="/signup">
                          <button className="btn-skew bg-[#FF3399] text-black font-bold px-6 py-3 hover:bg-white transition-all">
                            <span>Get This For Your Studio</span>
                          </button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding relative" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="badge-cyan inline-block mb-4">Features</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-white mb-4">
              Everything you need
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              One platform to showcase your wraps, capture leads, and close more sales.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-glow bg-zinc-900/50 border border-zinc-800 p-8 group"
              >
                <div className="w-12 h-12 bg-zinc-800 flex items-center justify-center mb-6 group-hover:bg-[#FF3399]/10 transition-colors">
                  <feature.icon className="w-6 h-6 text-[#FF3399]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-500">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Manufacturers */}
      <section className="py-16 px-6 lg:px-12 border-y border-zinc-800" data-testid="manufacturers-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="font-mono text-xs tracking-widest text-zinc-500 mb-4">TRUSTED BY THE BEST</p>
            <h2 className="text-3xl font-black tracking-tighter text-white mb-3">
              460+ pre-loaded wrap colors
            </h2>
            <p className="text-zinc-400 max-w-lg mx-auto">
              From the world's leading manufacturers, plus <span className="text-[#FF3399]">unlimited custom wraps</span> you can add yourself.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 items-center mb-8">
            {[
              { name: '3M', wraps: '149' },
              { name: 'Avery Dennison', wraps: '70' },
              { name: 'KPMF', wraps: '101' },
              { name: 'Oracal', wraps: '132' },
              { name: 'Arlon', wraps: '10' },
              { name: 'Hexis', wraps: 'PPF' },
            ].map((manufacturer, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-4 bg-zinc-900/30 border border-zinc-800 hover:border-[#FF3399]/30 transition-colors group"
              >
                <div className="h-8 flex items-center justify-center mb-2">
                  <span className="text-lg font-black text-zinc-400 group-hover:text-white transition-colors">{manufacturer.name}</span>
                </div>
                <p className="text-xs text-zinc-600 font-mono">{manufacturer.wraps} WRAPS</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-zinc-900/30 relative" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="badge-lime inline-block mb-4">How It Works</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
              Three simple steps
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Upload Photo', desc: 'Customer uploads their actual car photo from any angle' },
              { step: '02', title: 'Pick Wrap', desc: 'Select from your curated catalog of wrap colors and finishes' },
              { step: '03', title: 'Preview', desc: 'AI generates a photoreal visualization in under 60 seconds' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
              >
                <div className="text-[120px] font-black text-zinc-800/50 absolute -top-8 -left-4 leading-none select-none">
                  {item.step}
                </div>
                <div className="relative pt-16 pl-4">
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-zinc-500">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="section-padding relative overflow-hidden" data-testid="pricing-section">
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="badge-cyan inline-block mb-4">Pricing</div>
            <h2 className="text-5xl md:text-6xl font-black tracking-tighter text-white mb-4">
              €49<span className="text-zinc-600">/month</span>
            </h2>
            <p className="text-zinc-400 mb-8 text-lg">Starting at. Cancel anytime. No contracts.</p>
            <Link to="/pricing">
              <button className="btn-skew bg-[#FF3399] text-black font-bold uppercase tracking-wider px-10 py-5 hover:bg-white transition-all animate-pulse-glow">
                <span className="flex items-center gap-2">
                  View All Plans
                  <ArrowRight className="w-5 h-5" />
                </span>
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-b from-zinc-900/50 to-[#050505]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-white mb-6">
              Ready to transform your<br />
              <span className="text-[#FF3399]">wrap studio?</span>
            </h2>
            <p className="text-zinc-400 mb-8 text-lg max-w-xl mx-auto">
              Join studios across the world using WrapViz to close more high-ticket wraps.
            </p>
            <Link to="/signup">
              <button className="btn-skew bg-white text-black font-bold uppercase tracking-wider px-10 py-5 hover:bg-[#FF3399] transition-all glow-pink">
                <span className="flex items-center gap-2">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" />
                </span>
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-bold text-white">WrapViz</span>
            </div>
            <div className="flex gap-8 text-sm text-zinc-500">
              <Link to="/pricing" className="hover:text-[#FF3399] transition-colors">Pricing</Link>
              <Link to="/about" className="hover:text-[#FF3399] transition-colors">About</Link>
              <Link to="/privacy" className="hover:text-[#FF3399] transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-[#FF3399] transition-colors">Terms</Link>
              <Link to="/impressum" className="hover:text-[#FF3399] transition-colors">Impressum</Link>
            </div>
            <p className="font-mono-specs text-zinc-600">© 2025 WrapViz by Embermotors UG</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
